// src/components/Admin/LinkEditor.js
import React, { useState, useEffect, useCallback } from 'react';

export default function LinkEditor({ token }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [message, setMessage] = useState('');

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/links', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        // Handle both { links: [...] } and plain [...]
        const linkArray = Array.isArray(data) ? data : (data.links || []);
        console.log('[ADMIN] Loaded links:', linkArray.length);
        setLinks(linkArray);
      } else {
        console.error('[ADMIN] Failed to load links:', res.status);
        setLinks([]);
      }
    } catch (err) {
      console.error('[ADMIN] Error loading links:', err);
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const emptyLink = {
    title: '',
    url: '',
    description: '',
    category: 'general',
  };

  async function saveLink(linkData) {
    try {
      const isNew = !linkData.id;
      const url = isNew ? '/api/links' : `/api/links/${linkData.id}`;
      const method = isNew ? 'POST' : 'PUT';

      console.log(`[ADMIN] ${method} ${url}`);

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(linkData),
      });

      if (res.ok) {
        setEditing(null);
        setMessage(isNew ? 'Link created!' : 'Link updated!');
        setTimeout(() => setMessage(''), 3000);
        fetchLinks();
      } else {
        const err = await res.json();
        setMessage(`Error: ${err.error || 'Failed to save'}`);
      }
    } catch (err) {
      console.error('[ADMIN] Save link error:', err);
      setMessage('Error connecting to server');
    }
  }

  async function deleteLink(id) {
    if (!window.confirm('Delete this link?')) return;

    try {
      const res = await fetch(`/api/links/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (res.ok) {
        setMessage('Link deleted');
        setTimeout(() => setMessage(''), 3000);
        fetchLinks();
      } else {
        setMessage('Failed to delete link');
      }
    } catch (err) {
      setMessage('Error connecting to server');
    }
  }

  // Show loading state
  if (loading) {
    return (
      <div style={{ color: '#555', padding: '20px', textAlign: 'center' }}>
        Loading links...
      </div>
    );
  }

  // Show edit form
  if (editing) {
    return (
      <LinkForm
        link={editing}
        onSave={saveLink}
        onCancel={() => setEditing(null)}
        message={message}
      />
    );
  }

  // Show link list
  return (
    <div>
      {message && (
        <div style={{
          padding: '10px 15px',
          marginBottom: '15px',
          border: `1px solid ${message.includes('Error') ? '#ff0040' : '#00ff41'}`,
          color: message.includes('Error') ? '#ff0040' : '#00ff41',
        }}>
          {message}
        </div>
      )}

      <button
        className="bbs-button"
        onClick={() => setEditing({ ...emptyLink })}
        style={{ marginBottom: '20px' }}
      >
        + NEW LINK
      </button>

      {links.length === 0 ? (
        <div style={{ color: '#555', padding: '20px' }}>
          No links yet. Click "+ NEW LINK" to create one.
        </div>
      ) : (
        <div>
          {links.map(link => {
            // Safety: skip any malformed entries
            if (!link || !link.id) return null;

            return (
              <div
                key={link.id}
                style={{
                  border: '1px solid #333',
                  padding: '12px 15px',
                  marginBottom: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ color: '#00e5ff', fontWeight: 'bold' }}>
                      {link.title || 'Untitled'}
                    </span>
                    {link.category && (
                      <span style={{
                        color: '#ffb000',
                        fontSize: '0.7rem',
                        padding: '2px 6px',
                        border: '1px solid #ffb000',
                      }}>
                        {link.category.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div style={{ color: '#555', fontSize: '0.8rem', marginTop: '4px' }}>
                    {link.url}
                  </div>
                  {link.description && (
                    <div style={{ color: '#444', fontSize: '0.75rem', marginTop: '2px' }}>
                      {link.description}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '8px', marginLeft: '15px' }}>
                  <button
                    className="bbs-button"
                    onClick={() => setEditing({ ...link })}
                    style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                  >
                    EDIT
                  </button>
                  <button
                    className="bbs-button"
                    onClick={() => deleteLink(link.id)}
                    style={{
                      padding: '4px 10px',
                      fontSize: '0.75rem',
                      borderColor: '#ff0040',
                      color: '#ff0040',
                    }}
                  >
                    DEL
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Link edit form
function LinkForm({ link, onSave, onCancel, message }) {
  const [form, setForm] = useState({
    id: (link && link.id) || null,
    title: (link && link.title) || '',
    url: (link && link.url) || '',
    description: (link && link.description) || '',
    category: (link && link.category) || 'general',
  });

  function handleChange(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) {
      alert('Title is required');
      return;
    }
    if (!form.url.trim()) {
      alert('URL is required');
      return;
    }
    onSave(form);
  }

  return (
    <form onSubmit={handleSubmit}>
      {message && (
        <div style={{
          padding: '10px 15px',
          marginBottom: '15px',
          border: `1px solid ${message.includes('Error') ? '#ff0040' : '#00ff41'}`,
          color: message.includes('Error') ? '#ff0040' : '#00ff41',
        }}>
          {message}
        </div>
      )}

      <h3 style={{ color: '#00e5ff', marginBottom: '20px' }}>
        {form.id ? 'EDIT LINK' : 'NEW LINK'}
      </h3>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ color: '#ffb000', display: 'block', marginBottom: '5px', fontSize: '0.85rem' }}>
          TITLE *
        </label>
        <input
          type="text"
          value={form.title}
          onChange={e => handleChange('title', e.target.value)}
          className="admin-input"
          placeholder="Link title"
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ color: '#ffb000', display: 'block', marginBottom: '5px', fontSize: '0.85rem' }}>
          URL *
        </label>
        <input
          type="text"
          value={form.url}
          onChange={e => handleChange('url', e.target.value)}
          className="admin-input"
          placeholder="https://..."
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ color: '#ffb000', display: 'block', marginBottom: '5px', fontSize: '0.85rem' }}>
          DESCRIPTION
        </label>
        <input
          type="text"
          value={form.description}
          onChange={e => handleChange('description', e.target.value)}
          className="admin-input"
          placeholder="Brief description"
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ color: '#ffb000', display: 'block', marginBottom: '5px', fontSize: '0.85rem' }}>
          CATEGORY
        </label>
        <select
          value={form.category}
          onChange={e => handleChange('category', e.target.value)}
          className="admin-input"
          style={{
            width: '100%',
            background: '#0a0a0a',
            color: '#00ff41',
            border: '1px solid #333',
            padding: '8px',
            fontFamily: "'VT323', 'Courier New', monospace",
          }}
        >
          <option value="general">General</option>
          <option value="social">Social</option>
          <option value="writing">Writing</option>
          <option value="project">Project</option>
          <option value="resource">Resource</option>
        </select>
      </div>

      <div style={{ display: 'flex', gap: '10px' }}>
        <button type="submit" className="bbs-button">
          {form.id ? 'SAVE CHANGES' : 'CREATE LINK'}
        </button>
        <button
          type="button"
          className="bbs-button"
          onClick={onCancel}
          style={{ borderColor: '#555', color: '#555' }}
        >
          CANCEL
        </button>
      </div>
    </form>
  );
}